import { Check, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import type { NonConformity } from '@obraplan/core/domain/quality/non-conformity';
import { isAreaUnitCode } from '@obraplan/core/domain/schedule/unit-of-measure';
import { Input, Textarea } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { AreaMeasurementButton } from './AreaMeasurementDialog';
import { AttachmentGallery } from './AttachmentGallery';
import { PhotoSourceButtons } from './PhotoSourceButtons';
import { samePlaceKey, type PlaceOption, type RoomOfDay } from './nc-places';
import { parseQuantityText, type AnswerDraft } from './quality-verification-draft';
import type { NonConformityPhotos } from './QualityVerificationStep';

/**
 * A medição desta tela num serviço (Fase 37): de quem são as NCs que ela abrir, quanto ela
 * mede, e a unidade. É o teto da quantidade não conforme (RN-473) e o que decide se o campo
 * ganha o botão "Pela medida" (RN-379).
 */
export interface StepMeasurement {
  /** O dono das NCs desta medição neste serviço — `A:<alocação>`, `T:<medição da equipe>`. */
  readonly ownerKey?: string | undefined;
  /** O que esta medição mede, como número. Ausente: ainda não mediu. */
  readonly measured?: number | undefined;
  readonly unitName?: string | undefined;
  readonly unitCode?: string | undefined;
  readonly unitDecimals?: number | undefined;
  /**
   * As peças e os cômodos que o modal marcou agora e ainda não gravou (Fase 37, D9): entram na
   * lista do "onde" junto com os que o servidor já sabe do dia.
   */
  readonly draftPieces?: readonly string[] | undefined;
  readonly draftRooms?: readonly RoomOfDay[] | undefined;
}

function formatar(valor: number, casas: number | undefined): string {
  return valor.toLocaleString('pt-BR', {
    minimumFractionDigits: casas ?? 0,
    maximumFractionDigits: casas ?? 6,
  });
}

/**
 * Os campos da não conformidade **desta medição** num item reprovado (Fase 37, D7): abrem
 * abaixo do item, no mesmo modal.
 *
 * - **O que foi encontrado** — obrigatório, como sempre (RN-220);
 * - **a quantidade não conforme** — opcional, na unidade do serviço; no m², com o botão "Pela
 *   medida" (RN-473, RN-379). Ela sai da produção (RN-475);
 * - **a solução** — opcional: escrita, a NC nasce resolvida (RN-476);
 * - **as fotos** — opcionais, várias, da câmera ou da galeria. Antes de a NC existir elas
 *   esperam no rascunho e sobem depois de gravar (RN-479); a NC já gravada mostra a galeria
 *   dela, onde a foto entra na hora.
 */
export function NonConformityFields({
  itemLabel,
  answer,
  disabled,
  measurement,
  saved,
  photos,
  placeOptions,
  placesLoading = false,
  onChange,
}: {
  readonly itemLabel: string;
  readonly answer: AnswerDraft;
  readonly disabled: boolean;
  readonly measurement?: StepMeasurement | undefined;
  /** A NC desta medição já gravada no item — o número e a galeria dela. */
  readonly saved?: NonConformity | undefined;
  readonly photos?: NonConformityPhotos | undefined;
  /**
   * As peças e os cômodos do dia que a NC pode apontar (D9). `undefined`: a tela não oferece
   * lugar, ou ainda está lendo (`placesLoading`).
   */
  readonly placeOptions?: readonly PlaceOption[] | undefined;
  readonly placesLoading?: boolean | undefined;
  readonly onChange: (mudanca: Partial<AnswerDraft>) => void;
}) {
  const id = useId();
  const quantidade = parseQuantityText(answer.quantity);
  const medido = measurement?.measured;
  const passa = typeof quantidade === 'number' && medido !== undefined && quantidade > medido;
  const unidade = measurement?.unitName;
  const casas = measurement?.unitDecimals;

  return (
    <div className="flex flex-col gap-3 rounded-md border border-(--color-border-subtle) bg-(--color-surface) p-3">
      <label className="flex flex-col gap-1 text-xs text-(--color-content-muted)">
        O que foi encontrado
        {saved !== undefined && ` (NC nº ${String(saved.number)} — o texto de agora substitui)`}
        <Textarea
          aria-label={`Não conformidade — ${itemLabel}`}
          className="min-h-16"
          rows={2}
          disabled={disabled}
          value={answer.description}
          onChange={(event) => onChange({ description: event.target.value })}
        />
      </label>

      <div className="flex flex-col gap-1">
        <label htmlFor={`${id}-quantidade`} className="text-xs text-(--color-content-muted)">
          Quantidade não conforme <span className="font-normal">(opcional)</span>
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            id={`${id}-quantidade`}
            aria-label={`Quantidade não conforme — ${itemLabel}`}
            className={cn(
              'h-11 w-32 text-right tabular md:h-9',
              (quantidade === null || passa) && 'border-(--color-state-danger)',
            )}
            inputMode="decimal"
            placeholder="0"
            disabled={disabled}
            value={answer.quantity}
            aria-invalid={quantidade === null || passa || undefined}
            onChange={(event) => onChange({ quantity: event.target.value })}
          />
          {unidade !== undefined && (
            <span className="text-sm text-(--color-content-muted)">{unidade}</span>
          )}
          {unidade !== undefined && isAreaUnitCode(measurement?.unitCode) && (
            <AreaMeasurementButton
              subject={`não conforme — ${itemLabel}`}
              unitName={unidade}
              decimals={casas ?? 2}
              disabled={disabled}
              onApply={(total) => onChange({ quantity: formatar(total, casas) })}
            />
          )}
        </div>
        <p
          className={cn(
            'text-xs',
            quantidade === null || passa
              ? 'text-(--color-state-danger)'
              : 'text-(--color-content-muted)',
          )}
        >
          {quantidade === null
            ? 'Escreva um número maior que zero.'
            : passa
              ? `Passa do que foi medido (${formatar(medido ?? 0, casas)}${unidade === undefined ? '' : ` ${unidade}`}).`
              : medido === undefined
                ? 'Sai da produção do dia. Meça a quantidade executada para poder informá-la.'
                : `Uma parte dos ${formatar(medido, casas)}${unidade === undefined ? '' : ` ${unidade}`} medidos: ela sai da produção do dia.`}
        </p>
      </div>

      {(placesLoading || (placeOptions !== undefined && placeOptions.length > 0)) && (
        <OndeFoiEncontrada
          itemLabel={itemLabel}
          options={placeOptions ?? []}
          loading={placesLoading}
          selected={answer.places}
          disabled={disabled}
          onChange={(places) => onChange({ places })}
        />
      )}

      <label className="flex flex-col gap-1 text-xs text-(--color-content-muted)">
        <span>
          Solução aplicada <span className="font-normal">(opcional)</span>
        </span>
        <Textarea
          aria-label={`Solução aplicada — ${itemLabel}`}
          className="min-h-16"
          rows={2}
          disabled={disabled}
          placeholder="O que foi feito para corrigir."
          value={answer.solution}
          onChange={(event) => onChange({ solution: event.target.value })}
        />
        <span>
          {answer.solution.trim() === ''
            ? 'Sem a solução, a não conformidade fica aberta, para tratar depois.'
            : 'Com a solução, a não conformidade já entra resolvida.'}
        </span>
      </label>

      {saved !== undefined && photos !== undefined ? (
        <div>
          <p className="mb-1 text-xs text-(--color-content-muted)">Fotos da NC nº {saved.number}</p>
          <AttachmentGallery
            attachments={photos.of(saved.id)?.attachments ?? []}
            usage={photos.of(saved.id)?.usage}
            canAttach={photos.canAttach}
            canRemove={photos.canRemove}
            isPending={photos.isPending}
            resolveUrl={photos.resolveUrl}
            onAttach={(files) => photos.onAttach(saved.id, files)}
            onRemove={photos.onRemove}
          />
        </div>
      ) : (
        <FotosPendentes
          itemLabel={itemLabel}
          files={answer.photos}
          disabled={disabled}
          onChange={(files) => onChange({ photos: files })}
        />
      )}
    </div>
  );
}

/**
 * As fotos escolhidas antes de a NC existir (RN-479): ficam no rascunho, com a miniatura, e
 * sobem quando a NC ganha número. "Tirar foto" abre a câmera e "Anexar" a galeria, como em
 * toda galeria (`PhotoSourceButtons`).
 */
function FotosPendentes({
  itemLabel,
  files,
  disabled,
  onChange,
}: {
  readonly itemLabel: string;
  readonly files: readonly File[];
  readonly disabled: boolean;
  readonly onChange: (files: readonly File[]) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <PhotoSourceButtons
          accept="image/*"
          compact
          disabled={disabled}
          label={itemLabel}
          onFiles={(escolhidas) => onChange([...files, ...escolhidas])}
        />
        <span className="text-xs text-(--color-content-muted)">
          {files.length === 0
            ? 'Opcional. Sobem junto com a não conformidade.'
            : `${String(files.length)} ${files.length === 1 ? 'foto' : 'fotos'} — sobem ao gravar.`}
        </span>
      </div>
      {files.length > 0 && (
        <ul aria-label={`Fotos escolhidas — ${itemLabel}`} className="flex flex-wrap gap-2">
          {files.map((file, indice) => (
            <li key={`${file.name}-${String(indice)}`} className="relative">
              <Miniatura file={file} />
              <button
                type="button"
                aria-label={`Tirar a foto ${file.name}`}
                disabled={disabled}
                className="absolute -top-2 -right-2 flex size-6 items-center justify-center rounded-full border border-(--color-border-subtle) bg-(--color-surface) shadow-sm"
                onClick={() => onChange(files.filter((_, outro) => outro !== indice))}
              >
                <X className="size-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A miniatura de um arquivo local; o endereço é devolvido ao sair da tela. */
function Miniatura({ file }: { readonly file: File }) {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (typeof URL.createObjectURL !== 'function') return undefined;
    const criado = URL.createObjectURL(file);
    setUrl(criado);
    return () => URL.revokeObjectURL(criado);
  }, [file]);
  return url === undefined ? (
    <span className="flex size-16 items-center justify-center rounded-md border border-(--color-border-subtle) px-1 text-center text-[10px] break-all text-(--color-content-muted)">
      {file.name}
    </span>
  ) : (
    <img
      src={url}
      alt={file.name}
      className="size-16 rounded-md border border-(--color-border-subtle) object-cover"
    />
  );
}

/**
 * Onde a NC foi encontrada (D9): as peças executadas e os cômodos com avanço apontado naquele
 * dia. Marcar desfaz o que eles diziam — a peça volta a não executada, o cômodo ao percentual
 * de antes do dia — até serem apontados de novo, no dia em que o retrabalho for feito.
 */
function OndeFoiEncontrada({
  itemLabel,
  options,
  loading,
  selected,
  disabled,
  onChange,
}: {
  readonly itemLabel: string;
  readonly options: readonly PlaceOption[];
  readonly loading: boolean;
  readonly selected: readonly string[];
  readonly disabled: boolean;
  readonly onChange: (places: readonly string[]) => void;
}) {
  const marcado = (key: string) => selected.some((outra) => samePlaceKey(outra, key));
  const alternar = (key: string) =>
    onChange(
      marcado(key) ? selected.filter((outra) => !samePlaceKey(outra, key)) : [...selected, key],
    );
  const pecas = options.filter((option) => option.kind === 'PIECE');
  const comodos = options.filter((option) => option.kind === 'ROOM');
  const grupo = (titulo: string, lista: readonly PlaceOption[]) =>
    lista.length === 0 ? null : (
      <div className="flex flex-col gap-1">
        <span className="text-xs text-(--color-content-muted)">{titulo}</span>
        <ul className="flex flex-wrap gap-1.5" aria-label={`${titulo} — ${itemLabel}`}>
          {lista.map((option) => (
            <li key={option.key}>
              <button
                type="button"
                aria-pressed={marcado(option.key)}
                disabled={disabled}
                onClick={() => alternar(option.key)}
                className="inline-flex min-h-9 items-center gap-1 rounded-full border border-(--color-border-subtle) bg-(--color-surface-raised) px-3 py-1 text-xs text-(--color-content) transition-colors aria-pressed:border-(--color-state-danger) aria-pressed:bg-(--color-surface-muted) aria-pressed:font-medium md:min-h-7"
              >
                {marcado(option.key) && <Check aria-hidden="true" className="size-3.5" />}
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-xs text-(--color-content-muted)">
        Onde foi encontrada <span className="font-normal">(opcional)</span>
      </legend>
      {loading ? (
        <p className="text-xs text-(--color-content-muted)">Lendo as peças e os cômodos do dia…</p>
      ) : (
        <>
          {grupo('Peças executadas no dia', pecas)}
          {grupo('Cômodos com avanço no dia', comodos)}
          <p className="text-xs text-(--color-content-muted)">
            {selected.length === 0
              ? 'Marque o que não ficou conforme: a peça volta a não executada, e o cômodo ao percentual de antes do dia, até serem apontados de novo.'
              : `${String(selected.length)} ${selected.length === 1 ? 'marcado' : 'marcados'} — voltam ao que eram antes do dia até serem apontados de novo.`}
          </p>
        </>
      )}
    </fieldset>
  );
}
